import React, { useEffect, useState } from 'react';
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
} from 'recharts';
import { transformVisitStatsData } from '../utils/transformAPIResponse';
import { Box, Typography } from '@mui/material';
import humanizeDuration from 'humanize-duration';

interface VisitStatsChartProps {
	apiResponse: AnalyticsApiResponse | null;
}

const VisitStatsChart: React.FC<VisitStatsChartProps> = ({ apiResponse }) => {
	const [chartData, setChartData] = useState<VisitStatsData[]>([]);

	// Transform the data when the component mounts or when the API data changes
	useEffect(() => {
		if (!apiResponse) return;
		const transformed = transformVisitStatsData(apiResponse);
		setChartData(transformed);
	}, [apiResponse]);

	// Explanation for each data key
	const dataExplanations = {
		sessions:
			'Number of individual visits to the site during a specific period.',
		activeUsers:
			'The count of unique users who are actively engaging with the site.',
		bounceRate:
			'The percentage of visitors who navigate away from the site after viewing only one page.',
		engagementDuration:
			'The average amount of time users spend interacting with the site.',
	};

	// Common syncId for synchronization
	const syncId = 'anyUniqueId';

	return (
		<Box m={2}>
			{/* Chart 1: Active Users and Sessions */}
			<Box mb={4}>
				<Typography>
					<strong>Sessions</strong>: {dataExplanations.sessions}
				</Typography>
				<Typography>
					<strong>Active Users</strong>:{' '}
					{dataExplanations.activeUsers}
				</Typography>
				<ResponsiveContainer width="100%" height={250}>
					<LineChart
						data={chartData}
						syncId={syncId}
						margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
					>
						<CartesianGrid strokeDasharray="3 3" />
						<XAxis dataKey="date" />
						<YAxis
							label={{
								value: 'Count',
								angle: -90,
								position: 'insideLeft',
							}}
						/>
						<Tooltip />
						<Legend />
						<Line
							type="monotone"
							dataKey="activeUsers"
							stroke="#82ca9d"
							name="Active Users"
						/>
						<Line
							type="monotone"
							dataKey="sessions"
							stroke="#8884d8"
							name="Sessions"
						/>
					</LineChart>
				</ResponsiveContainer>
			</Box>

			{/* Chart 2: Bounce Rate */}
			<Box mb={4}>
				<Typography>
					<strong>Bounce Rate</strong>: {dataExplanations.bounceRate}
				</Typography>
				<ResponsiveContainer width="100%" height={250}>
					<LineChart
						data={chartData}
						syncId={syncId}
						margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
					>
						<CartesianGrid strokeDasharray="3 3" />
						<XAxis dataKey="date" />
						<YAxis
							label={{
								value: 'Bounce Rate (%)',
								angle: -90,
								position: 'insideLeft',
							}}
							domain={[0, 100]}
						/>
						<Tooltip
							formatter={(value) => [`${value}%`, 'Bounce Rate']}
							labelFormatter={(label) => `Date: ${label}`}
						/>
						<Legend />
						<Line
							type="monotone"
							dataKey="bounceRate"
							stroke="#ff7300"
							name="Bounce Rate"
						/>
					</LineChart>
				</ResponsiveContainer>
			</Box>

			{/* Chart 3: Engagement Duration */}
			<Box mb={4}>
				<Typography>
					<strong>Engagement Duration</strong>:{' '}
					{dataExplanations.engagementDuration}
				</Typography>
				<ResponsiveContainer width="100%" height={250}>
					<LineChart
						data={chartData}
						syncId={syncId}
						margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
					>
						<CartesianGrid strokeDasharray="3 3" />
						<XAxis dataKey="date" />
						<YAxis
							label={{
								value: 'Duration',
								angle: -90,
								position: 'insideLeft',
							}}
						/>
						<Tooltip
							formatter={(value) => [
								humanizeDuration(Number(value) * 1000, {
									largest: 2,
									round: true,
								}),
								'Engagement Duration',
							]}
							labelFormatter={(label) => `Date: ${label}`}
						/>
						<Legend />
						<Line
							type="monotone"
							dataKey="engagementDuration"
							stroke="#387908"
							name="Engagement Duration"
						/>
					</LineChart>
				</ResponsiveContainer>
			</Box>
		</Box>
	);
};

export default VisitStatsChart;
